/**
 * Team challenges API (P3.5) — RTK Query endpoints for /challenges.
 *
 * The server decides the workspace (the token's), who may create (Central's
 * training:write) and every progress number (canonical results only). Nothing
 * the client sends is an identity.
 */

import { api } from '../api';
import { withRetryAfter } from '../../utils/loadError';

import type {
  ChallengeDetail,
  ChallengeSummary,
  CreateChallengeRequest,
} from '../../components/challenges/challengeTypes';

export type {
  ChallengeCondition,
  ChallengeDetail,
  ChallengeSummary,
  CreateChallengeRequest,
  LeaderboardEntry,
  ParticipantProgress,
} from '../../components/challenges/challengeTypes';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

// The tag is registered here, in this slice, not on the shared tagTypes line.
export const challengesApi = api.enhanceEndpoints({ addTagTypes: ['Challenges'] }).injectEndpoints({
  endpoints: (builder) => ({
    listChallenges: builder.query<ChallengeSummary[], void>({
      query: () => '/challenges',
      transformResponse: (r: ApiEnvelope<ChallengeSummary[]>) => r.data,
      // The list page tells "busy" (429, with Retry-After) from "failed" from empty.
      transformErrorResponse: (error, meta) => withRetryAfter(error, meta),
      providesTags: ['Challenges'],
    }),

    getChallenge: builder.query<ChallengeDetail, string>({
      query: (id) => `/challenges/${id}`,
      transformResponse: (r: ApiEnvelope<ChallengeDetail>) => r.data,
      // Headers are not visible to the hook: carry Retry-After on the error so
      // the page can tell "busy, try again in N s" from "not found".
      transformErrorResponse: (error, meta) => withRetryAfter(error, meta),
      providesTags: (_r, _e, id) => [{ type: 'Challenges', id }],
    }),

    createChallenge: builder.mutation<ChallengeSummary, CreateChallengeRequest>({
      query: (body) => ({ url: '/challenges', method: 'POST', body }),
      transformResponse: (r: ApiEnvelope<ChallengeSummary>) => r.data,
      invalidatesTags: ['Challenges'],
    }),

    joinChallenge: builder.mutation<ChallengeDetail, { id: string; shareOnLeaderboard: boolean }>({
      query: ({ id, shareOnLeaderboard }) => ({ url: `/challenges/${id}/join`, method: 'POST', body: { shareOnLeaderboard } }),
      transformResponse: (r: ApiEnvelope<ChallengeDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => ['Challenges', { type: 'Challenges', id }],
    }),

    leaveChallenge: builder.mutation<ChallengeDetail, string>({
      query: (id) => ({ url: `/challenges/${id}/leave`, method: 'POST' }),
      transformResponse: (r: ApiEnvelope<ChallengeDetail>) => r.data,
      invalidatesTags: (_r, _e, id) => ['Challenges', { type: 'Challenges', id }],
    }),

    setChallengeSharing: builder.mutation<ChallengeDetail, { id: string; shareOnLeaderboard: boolean }>({
      query: ({ id, shareOnLeaderboard }) => ({ url: `/challenges/${id}/sharing`, method: 'PATCH', body: { shareOnLeaderboard } }),
      transformResponse: (r: ApiEnvelope<ChallengeDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Challenges', id }],
    }),

    archiveChallenge: builder.mutation<ChallengeSummary, string>({
      query: (id) => ({ url: `/challenges/${id}/archive`, method: 'POST' }),
      transformResponse: (r: ApiEnvelope<ChallengeSummary>) => r.data,
      invalidatesTags: (_r, _e, id) => ['Challenges', { type: 'Challenges', id }],
    }),
  }),
});

export const {
  useListChallengesQuery,
  useGetChallengeQuery,
  useCreateChallengeMutation,
  useJoinChallengeMutation,
  useLeaveChallengeMutation,
  useSetChallengeSharingMutation,
  useArchiveChallengeMutation,
} = challengesApi;
